<script lang="ts">
  import { onDestroy, onMount } from "svelte";

  const MAX_CANDLES = 80;
  const CANDLE_W = 8;
  const CANDLE_GAP = 2;
  const W = 900;
  const H = 300;
  const PAD_T = 10;
  const PAD_B = 10;
  const PAD_L = 0;
  const PAD_R = 0;
  const PLOT_W = W - PAD_L - PAD_R;
  const PLOT_H = H - PAD_T - PAD_B;
  const STEP = CANDLE_W + CANDLE_GAP;

  type FakeCandle = {
    open: number;
    high: number;
    low: number;
    close: number;
    signal?: "buy" | "sell";
  };

  let candles: FakeCandle[] = $state([]);
  let price = 100;
  let iv: any;

  function nextCandle(): FakeCandle {
    const volatility = 0.8 + Math.random() * 1.2;
    const drift = (Math.random() - 0.48) * volatility;
    const open = price;
    const close = open + drift;
    const wickUp = Math.abs(drift) * (0.3 + Math.random() * 0.8);
    const wickDown = Math.abs(drift) * (0.3 + Math.random() * 0.8);
    const high = Math.max(open, close) + wickUp;
    const low = Math.min(open, close) - wickDown;
    price = close;

    // Mean-revert if price drifts too far
    if (price > 120) price -= 0.5;
    if (price < 80) price += 0.5;

    let signal: "buy" | "sell" | undefined;
    const r = Math.random();
    if (r < 0.04) signal = "buy";
    else if (r < 0.08) signal = "sell";

    return { open, high, low, close, signal };
  }

  onMount(() => {
    // Seed initial candles
    for (let i = 0; i < MAX_CANDLES; i++) {
      candles.push(nextCandle());
    }

    iv = setInterval(() => {
      candles.push(nextCandle());
      if (candles.length > MAX_CANDLES) candles.shift();
      candles = candles; // trigger reactivity
    }, 80);
  });

  onDestroy(() => clearInterval(iv));

  let bars = $derived.by(() => {
    if (candles.length < 2) return [];

    let minP = Infinity;
    let maxP = -Infinity;
    for (const c of candles) {
      if (c.low < minP) minP = c.low;
      if (c.high > maxP) maxP = c.high;
    }
    const range = maxP - minP || 1;
    const scaleY = (p: number) => PAD_T + PLOT_H - ((p - minP) / range) * PLOT_H;

    const totalW = candles.length * STEP;
    const offsetX = PAD_L + PLOT_W - totalW;

    return candles.map((c, i) => {
      const x = offsetX + i * STEP;
      const bullish = c.close >= c.open;
      const bodyTop = scaleY(Math.max(c.open, c.close));
      const bodyBot = scaleY(Math.min(c.open, c.close));
      const bodyH = Math.max(1, bodyBot - bodyTop);
      return {
        x,
        wickTop: scaleY(c.high),
        wickBot: scaleY(c.low),
        bodyTop,
        bodyH,
        color: bullish ? "#22c55e" : "#ef4444",
        signal: c.signal,
        signalY: c.signal === "buy" ? scaleY(c.low) + 10 : scaleY(c.high) - 10,
      };
    });
  });
</script>

<svg viewBox="0 0 {W} {H}" class="w-full h-full" preserveAspectRatio="xMidYMid slice">
  <!-- Grid lines -->
  {#each [0.25, 0.5, 0.75] as frac}
    <line
      x1={0}
      y1={PAD_T + PLOT_H * frac}
      x2={W}
      y2={PAD_T + PLOT_H * frac}
      stroke="#374151"
      stroke-width="0.5"
      opacity="0.3"
    />
  {/each}

  <!-- Candles -->
  {#each bars as bar}
    <line
      x1={bar.x + CANDLE_W / 2}
      y1={bar.wickTop}
      x2={bar.x + CANDLE_W / 2}
      y2={bar.wickBot}
      stroke={bar.color}
      stroke-width="1"
    />
    <rect
      x={bar.x}
      y={bar.bodyTop}
      width={CANDLE_W}
      height={bar.bodyH}
      fill={bar.color}
      rx="0.5"
    />
    <!-- Buy/Sell signals -->
    {#if bar.signal === "buy"}
      <circle cx={bar.x + CANDLE_W / 2} cy={bar.signalY} r="6" fill="#22c55e" />
      <text x={bar.x + CANDLE_W / 2} y={bar.signalY + 3} text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="monospace">B</text>
    {:else if bar.signal === "sell"}
      <circle cx={bar.x + CANDLE_W / 2} cy={bar.signalY} r="6" fill="#ef4444" />
      <text x={bar.x + CANDLE_W / 2} y={bar.signalY + 3} text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="monospace">S</text>
    {/if}
  {/each}
</svg>
